/**
 * Editing the team document, in place (ADR-121).
 *
 * The Console used to hold a structured `TeamDraft` of its own and serialise it
 * into YAML. That model was a sketch — it knew about roles and transitions and
 * nothing about provider routes, workspaces, repositories, board mappings or
 * ingest — so anything it produced was rejected by the compiler, and every new
 * field in §13.1 would have had to be added twice.
 *
 * So the editor reads the **exact document** a version was approved as, changes
 * the parts it understands, and writes the rest back untouched. Two properties
 * follow, and both matter more than the code saved:
 *
 *  - **A field the editor does not know survives an edit.** Somebody adding
 *    `repositories` or a workflow override by hand does not lose it because
 *    they later renamed a role in the UI.
 *  - **There is one schema.** The compiler decides what is valid (AC-001);
 *    this file only knows where things live in the tree.
 *
 * Everything here is pure: parse, read, modify, serialise. The screen does the
 * asking, the server does the deciding.
 */

import { parse, stringify } from 'yaml';

/** What the editor understands about one agent. */
export interface RoleEdit {
  /** Unmodelled package-specific fields survive visual edits unchanged. */
  readonly raw?: Readonly<Record<string, unknown>>;
  /** Stable editor-only row identity; it is deliberately never serialized. */
  readonly draftKey: string;
  readonly id: string;
  readonly packageRef: string;
  /**
   * The agent program this role runs (ADR-122).
   *
   * The single most consequential field on a role, and the reason is what it
   * replaced. The platform no longer drives a model loop, so a provider route
   * decides very little; the harness decides what the agent *is* — which tool
   * loop, which MCP client, which conversation. Changing it changes everything
   * about how the work is done.
   */
  readonly harness: string;
  /**
   * The AI provider account this agent runs on (ADR-131).
   *
   * Empty means «not chosen», which is a real and temporary state: every team
   * saved before connections existed has it, and those teams keep working —
   * the agent runs on whatever account its machine has, exactly as before. The
   * compiler says so as a warning until somebody picks one here.
   */
  readonly connection: string;
  readonly providerRoute: string;
  readonly concurrency: number;
  readonly threadPolicy: 'persistent' | 'persistent-per-workspace' | 'ephemeral';
  readonly workspace: string;
  /** A saved Orca environment, or empty for the machine the daemon runs on. */
  readonly environment: string;
  /** The agent's own system prompt. Empty means the task brief alone. */
  readonly instructions: string;
  /** Model the program is started with. Empty = the signed-in account's default. */
  readonly model: string;
  readonly toolMode: 'default' | 'custom';
  readonly toolAllow: readonly string[];
  readonly toolDeny: readonly string[];
  /** A background role has no board-column declaration or synthesized stage. */
  readonly workflowMode: 'background' | 'board-worker';
  /**
   * The five answers that put this agent on the board (schema v11): take
   * from, work in, pass to, fail to, escalate to — plus how many fails before
   * escalation. Empty strings mean "not declared"; the screen shows the
   * running workflow's values as placeholders until somebody edits.
   */
  readonly colFrom: string;
  readonly colWorking: string;
  /** Maximum failed returns before escalation; null means the compiler default. */
  readonly colMaxFails: number | null;
  /** The exit rules, in order: condition in the operator's words → column. */
  readonly exits: readonly { readonly when: string; readonly to: string; readonly limit?: number }[];
}

/** What the editor understands about one integration — an MCP server or an API. */
export interface IntegrationEdit {
  readonly id: string;
  readonly type: 'mcp' | 'grafana' | 'http' | 'custom';
  readonly connectionRef: string;
  readonly roles: readonly string[];
  readonly endpoints: readonly string[];
  /** MCP only: how the server is started. */
  readonly command: string;
  /** MCP only: a remote HTTPS server, used instead of command. */
  readonly url: string;
  readonly args: readonly string[];
  readonly env: readonly { readonly name: string; readonly value: string }[];
}

export interface TeamEdit {
  readonly name: string;
  readonly roles: readonly RoleEdit[];
  /** Agent identities declared by stage-native worker pools and background agents. */
  readonly configuredAgentIds: readonly string[];
  readonly integrations: readonly IntegrationEdit[];
  /** Every provider route the document declares; a role must name one of these. */
  readonly providerRoutes: readonly string[];
  /** Every workspace template, plus the literal `none`. */
  readonly workspaces: readonly string[];
}

interface Doc {
  metadata?: { name?: unknown };
  spec?: {
    roles?: unknown;
    repositories?: unknown;
    integrations?: unknown;
    providerRoutes?: unknown;
    workspaces?: unknown;
    workflows?: unknown;
    workflow?: unknown;
    stages?: unknown;
    checklists?: unknown;
    backgroundAgents?: unknown;
    board?: unknown;
    mainOperator?: unknown;
    epicCoordinator?: unknown;
  };
}

/** The dedicated Epic Coordinator block, isolated without touching other team settings. */
export function readEpicCoordinatorSource(document: Doc): string {
  const value = document.spec?.epicCoordinator;
  return value === undefined ? '' : stringify({ epicCoordinator: value }, { lineWidth: 100 }).trimEnd();
}

/** Replaces only `spec.epicCoordinator`; credentials remain outside the document. */
export function writeEpicCoordinatorSource(document: Doc, source: string): Doc {
  const next = structuredClone(document) as Doc;
  next.spec ??= {};
  delete next.spec.epicCoordinator;
  if (source.trim() === '') return next;
  const parsed: unknown = parse(source);
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
    throw new Error('Epic Coordinator configuration must be a YAML mapping.');
  }
  const record = parsed as Record<string, unknown>;
  const unknown = Object.keys(record).filter((key) => key !== 'epicCoordinator');
  if (unknown.length > 0) throw new Error(`Epic Coordinator configuration contains unsupported keys: ${unknown.join(', ')}`);
  if (typeof record['epicCoordinator'] !== 'object' || record['epicCoordinator'] === null || Array.isArray(record['epicCoordinator'])) {
    throw new Error('epicCoordinator must be a YAML mapping.');
  }
  next.spec.epicCoordinator = record['epicCoordinator'];
  return next;
}

/** Adds the Console's dedicated persistent-home template when first creating the coordinator. */
export function ensureEpicCoordinatorWorkspace(document: Doc, id = 'epic-coordinator-home'): Doc {
  const next = structuredClone(document) as Doc;
  next.spec ??= {};
  if (text(next.spec.epicCoordinator, 'workspace') !== id) return next;
  const existing = asArray(next.spec.workspaces);
  if (existing.some((workspace) => text(workspace, 'id') === id)) return next;
  next.spec.workspaces = [
    ...existing,
    { id, strategy: 'git_worktree', isolation: 'trusted-local', retention: '30d' },
  ];
  return next;
}

export function parseTeamDocument(source: string): Doc {
  const parsed: unknown = parse(source);
  if (typeof parsed !== 'object' || parsed === null) {
    throw new Error('the stored team document is not a mapping');
  }
  return parsed as Doc;
}

const STAGE_CONFIGURATION_KEYS = ['workflow', 'stages', 'checklists', 'backgroundAgents', 'release'] as const;

/** The stage-native part of a team document, isolated for the Settings editor. */
export function readStageConfigurationSource(document: Doc): string {
  const spec = (document.spec ?? {}) as Record<string, unknown>;
  const selected: Record<string, unknown> = {};
  for (const key of STAGE_CONFIGURATION_KEYS) {
    const value = spec[key];
    if (value !== undefined) selected[key] = value;
  }
  return Object.keys(selected).length === 0 ? '' : stringify(selected, { lineWidth: 100 }).trimEnd();
}

/** Replaces only stage-native keys and preserves every unrelated team setting. */
export function writeStageConfigurationSource(document: Doc, source: string): Doc {
  const next = structuredClone(document) as Doc;
  next.spec ??= {};
  const spec = next.spec as Record<string, unknown>;
  for (const key of STAGE_CONFIGURATION_KEYS) delete spec[key];
  if (source.trim() === '') return next;
  const parsed: unknown = parse(source);
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
    throw new Error('stage configuration must be a YAML mapping');
  }
  const record = parsed as Record<string, unknown>;
  const unknown = Object.keys(record).filter((key) => !(STAGE_CONFIGURATION_KEYS as readonly string[]).includes(key));
  if (unknown.length > 0) throw new Error(`stage configuration contains unsupported keys: ${unknown.join(', ')}`);
  for (const key of STAGE_CONFIGURATION_KEYS) if (record[key] !== undefined) spec[key] = record[key];
  if (Array.isArray(record['stages'])) delete spec['roles'];
  return next;
}

/** Reads the parts the editor can show. Everything else stays in the tree. */
export function readTeam(document: Doc): TeamEdit {
  const spec = document.spec ?? {};
  const stageAgents = asArray(spec.stages)
    .filter((stage) => text(stage, 'kind') === 'agent')
    .map((stage) => text(stage, 'id'));
  const backgroundAgents = asArray(spec.backgroundAgents).map((agent) => text(agent, 'id'));
  // The Epic Coordinator is a singular first-class Agent, not a stage worker
  // or backgroundAgent. The compiler nevertheless accepts it in integration
  // bindings whenever its dedicated block exists, so the editor must expose
  // the same identity to its courtesy validation. Omitting it here made valid
  // documents look broken and disabled Save even though the server compiler
  // accepted the exact same configuration.
  const epicCoordinator = spec.epicCoordinator === undefined ? [] : ['epic-coordinator'];

  return {
    name: typeof document.metadata?.name === 'string' ? document.metadata.name : '',
    roles: (Array.isArray(spec.stages) ? asArray(spec.backgroundAgents) : asArray(spec.roles))
      .map((role, index) => readRole(role, `saved-${String(index + 1)}`)),
    configuredAgentIds: [...stageAgents, ...backgroundAgents, ...epicCoordinator].filter((id) => id.length > 0),
    integrations: asArray(spec.integrations).map(readIntegration),
    providerRoutes: asArray(spec.providerRoutes)
      .map((route) => text(route, 'id'))
      .filter((id) => id.length > 0),
    // `none` is a real value in §11.1 — a role without a filesystem — and it is
    // not in the workspaces list because it is not a template.
    workspaces: ['none', ...asArray(spec.workspaces).map((workspace) => text(workspace, 'id'))].filter(
      (id) => id.length > 0,
    ),
  };
}

/**
 * Writes the editable parts back, leaving everything else exactly as it was.
 *
 * A deep copy first: the caller keeps the parsed document to diff against, and
 * an in-place edit would make "what changed" unanswerable.
 */
export function writeTeam(document: Doc, edit: TeamEdit): Doc {
  const next = structuredClone(document) as Doc;
  next.spec ??= {};

  const legacyRoles = edit.roles.map((role) => {
    const written: Record<string, unknown> = {
    ...(role.raw ?? {}),
    id: role.id,
    package: role.packageRef,
    // Omitted when it matches the compiler's own default, so a document nobody
    // has changed does not grow a field asserting what was already true.
    ...(role.harness.trim().length === 0 || role.harness.trim() === 'claude'
      ? {}
      : { harness: role.harness.trim() }),
    // Omitted rather than written as an empty string: absent means "not chosen"
    // and the schema refuses an empty identifier, so writing one would turn an
    // unmigrated agent into a document that will not compile.
    ...(role.connection.trim().length === 0 ? {} : { connection: role.connection.trim() }),
    providerRoute: role.providerRoute,
    concurrency: role.concurrency,
    threadPolicy: role.threadPolicy,
    workspace: role.workspace,
    // Omitted rather than written as null: an absent placement means "the
    // daemon's own machine", and a null in the document would have to be
    // allowed by the schema to mean the same thing.
    ...(role.environment.trim().length === 0 ? {} : { placement: { environment: role.environment.trim() } }),
    ...(role.instructions.trim().length === 0 ? {} : { instructions: role.instructions }),
    ...(role.model.trim().length === 0 ? {} : { model: role.model.trim() }),
    ...(role.toolMode === 'custom' ? { toolAccess: { allow: [...role.toolAllow], ...(role.toolDeny.length === 0 ? {} : { deny: [...role.toolDeny] }) } } : {}),
    /*
     * Background is the only mode that omits columns. A board worker may omit
     * `working`: the compiler derives it from `from`. Keep that absence on a
     * round trip rather than materialising a duplicate declaration.
     */
    ...(role.workflowMode === 'background'
      ? {}
      : {
          columns: {
            from: role.colFrom.trim(),
            ...(role.colWorking.trim().length === 0 ? {} : { working: role.colWorking.trim() }),
            ...(role.colMaxFails === null ? {} : { maxFails: role.colMaxFails }),
            exits: role.exits
              .map((exit) => ({
                when: exit.when.trim(),
                to: exit.to.trim(),
                ...(exit.limit === undefined ? {} : { limit: exit.limit }),
              })),
          },
        }),
    };
    if (role.harness.trim().length === 0 || role.harness.trim() === 'claude') delete written['harness'];
    if (role.connection.trim().length === 0) delete written['connection'];
    if (role.environment.trim().length === 0) delete written['placement'];
    if (role.instructions.trim().length === 0) delete written['instructions'];
    if (role.model.trim().length === 0) delete written['model'];
    if (role.toolMode === 'default') delete written['toolAccess'];
    if (role.workflowMode === 'background') delete written['columns'];
    return written;
  });
  // Stage-native teams own their worker profiles under spec.stages. Keeping a
  // second editable role list would create two authorities for one agent.
  if (Array.isArray(next.spec.stages)) {
    delete next.spec.roles;
    const backgroundAgents = edit.roles.map((role) => {
      const written: Record<string, unknown> = {
        ...(role.raw ?? {}),
        id: role.id,
        package: role.packageRef,
        providerRoute: role.providerRoute,
        harness: role.harness.trim(),
        workspace: role.workspace,
        threadPolicy: role.threadPolicy,
        ...(role.connection.trim().length === 0 ? {} : { connection: role.connection.trim() }),
        ...(role.environment.trim().length === 0 ? {} : { placement: { environment: role.environment.trim() } }),
        ...(role.instructions.trim().length === 0 ? {} : { instructions: role.instructions }),
        ...(role.model.trim().length === 0 ? {} : { model: role.model.trim() }),
        ...(role.toolMode === 'custom' ? { toolAccess: { allow: [...role.toolAllow], ...(role.toolDeny.length === 0 ? {} : { deny: [...role.toolDeny] }) } } : {}),
      };
      // These belong only to the legacy RoleDocument shape. In particular,
      // backgroundAgents requires an explicit harness and rejects concurrency.
      delete written['concurrency'];
      delete written['columns'];
      if (role.connection.trim().length === 0) delete written['connection'];
      if (role.environment.trim().length === 0) delete written['placement'];
      if (role.instructions.trim().length === 0) delete written['instructions'];
      if (role.model.trim().length === 0) delete written['model'];
      if (role.toolMode === 'default') delete written['toolAccess'];
      return written;
    });
    if (backgroundAgents.length === 0) delete next.spec.backgroundAgents;
    else next.spec.backgroundAgents = backgroundAgents;
  } else next.spec.roles = legacyRoles;

  /*
   * Repository declarations are not editable on this screen, but release
   * targets still refer to their stable IDs. Preserve the section verbatim:
   * deleting it during an unrelated role/stage edit makes an otherwise valid
   * team impossible to compile and can silently remove release routing.
   */

  const integrations = edit.integrations.map((integration) => ({
    id: integration.id,
    type: integration.type,
    connectionRef: integration.connectionRef,
    roles: [...integration.roles],
    ...(integration.endpoints.length === 0 ? {} : { endpoints: [...integration.endpoints] }),
    ...(integration.command.trim().length === 0 ? {} : { command: integration.command.trim() }),
    ...(integration.url.trim().length === 0 ? {} : { url: integration.url.trim() }),
    ...(integration.args.length === 0 ? {} : { args: [...integration.args] }),
    ...(integration.env.length === 0
      ? {}
      : { env: Object.fromEntries(integration.env.filter((entry) => entry.name.length > 0).map((entry) => [entry.name, entry.value])) }),
  }));

  // An empty list is removed rather than written as `[]`: a document that never
  // had integrations should not grow an empty key because somebody opened the
  // editor and closed it.
  if (integrations.length === 0) delete next.spec.integrations;
  else next.spec.integrations = integrations;

  return next;
}

/**
 * Where one stage's claims take the card, as the *document* says (schema v11).
 *
 * Empty string means "no override — the workflow package's default", and the
 * editor renders the effective default beside it. Values are written under
 * `spec.workflows[0].overrides` as the compiler's own keys (`testing.fail.to`),
 * beside whatever override keys the operator wrote by hand — which survive,
 * because everything here edits keys it knows and leaves the rest.
 */
export interface RoutingEdit {
  /** The stage's override prefix — `review` for code-review, else the stage id. */
  readonly prefix: string;
  readonly passTo: string;
  readonly failTo: string;
  readonly escalateTo: string;
  /** Kept as text so a cleared field means "default", not zero. */
  readonly maxReturns: string;
}

function overridesOf(document: Doc): Record<string, unknown> {
  const workflows = document.spec?.workflows;
  const first = Array.isArray(workflows) ? (workflows[0] as Record<string, unknown> | undefined) : undefined;
  const overrides = first?.['overrides'];
  return typeof overrides === 'object' && overrides !== null ? (overrides as Record<string, unknown>) : {};
}

export function readRouting(document: Doc, prefixes: readonly string[]): readonly RoutingEdit[] {
  const overrides = overridesOf(document);
  const str = (key: string): string => (typeof overrides[key] === 'string' ? (overrides[key] as string) : '');
  return prefixes.map((prefix) => ({
    prefix,
    passTo: str(`${prefix}.pass.to`),
    failTo: str(`${prefix}.fail.to`),
    escalateTo: str(`${prefix}.escalateTo`),
    maxReturns: typeof overrides[`${prefix}.rework.sameBlockerMax`] === 'number' ? String(overrides[`${prefix}.rework.sameBlockerMax`]) : '',
  }));
}

export function writeRouting(document: Doc, routing: readonly RoutingEdit[]): Doc {
  const next = structuredClone(document) as Doc;
  next.spec ??= {};
  const workflows = Array.isArray(next.spec.workflows) ? (next.spec.workflows as Record<string, unknown>[]) : [];
  if (workflows.length === 0) {
    // A document with no workflow entry cannot hold overrides; writing one
    // from here would invent a package reference. The section stays read-only
    // for such documents and this write is a no-op.
    return next;
  }
  const first = workflows[0] as Record<string, unknown>;
  const overrides = { ...overridesOf(next) };

  for (const route of routing) {
    const set = (key: string, value: string): void => {
      // A cleared field removes the override — the default returns — rather
      // than writing an empty string the compiler would refuse.
      if (value.trim().length === 0) delete overrides[key];
      else overrides[key] = value.trim();
    };
    set(`${route.prefix}.pass.to`, route.passTo);
    set(`${route.prefix}.fail.to`, route.failTo);
    set(`${route.prefix}.escalateTo`, route.escalateTo);
    const returns = route.maxReturns.trim();
    if (returns.length === 0) delete overrides[`${route.prefix}.rework.sameBlockerMax`];
    else overrides[`${route.prefix}.rework.sameBlockerMax`] = Number(returns);
  }

  if (Object.keys(overrides).length === 0) delete first['overrides'];
  else first['overrides'] = overrides;
  next.spec.workflows = workflows;
  return next;
}

/* ---------------------------------------------------- operator program --- */

/** `spec.mainOperator.harness`, or empty for the built-in replies. */
export function readOperatorProgram(document: Doc): string {
  return text(document.spec?.mainOperator, 'harness');
}

/** Sets or clears the operator's agent program, touching nothing else. */
export function writeOperatorProgram(document: Doc, harness: string): Doc {
  const next = structuredClone(document) as Doc;
  next.spec ??= {};
  const operator =
    typeof next.spec.mainOperator === 'object' && next.spec.mainOperator !== null
      ? (next.spec.mainOperator as Record<string, unknown>)
      : {};
  if (harness.trim().length === 0) delete operator['harness'];
  else operator['harness'] = harness.trim();
  next.spec.mainOperator = operator;
  return next;
}

/** `mainOperator.model` — the model the operator's program runs; empty means the harness default. */
export function writeOperatorModel(document: Doc, model: string): Doc {
  const next = structuredClone(document) as Doc;
  next.spec ??= {};
  const operator =
    typeof next.spec.mainOperator === 'object' && next.spec.mainOperator !== null
      ? (next.spec.mainOperator as Record<string, unknown>)
      : {};
  if (model.trim().length === 0) delete operator['model'];
  else operator['model'] = model.trim();
  next.spec.mainOperator = operator;
  return next;
}

/* ------------------------------------------------------- board columns --- */

/** The three columns every board has; not editable, not stored. */
export const BOARD_GIVENS = { first: 'backlog', last: ['done', 'human-review'] } as const;

/** `spec.board.columns` — the person's ordering of the middle columns. */
export function readBoardColumns(document: Doc): readonly string[] {
  const board = document.spec?.board;
  if (typeof board !== 'object' || board === null) return [];
  const columns = (board as Record<string, unknown>)['columns'];
  return Array.isArray(columns) ? columns.filter((entry): entry is string => typeof entry === 'string') : [];
}

/** `spec.board.hidden` — columns the board keeps out of sight by default. */
export function readHiddenColumns(document: Doc): readonly string[] {
  const board = document.spec?.board;
  if (typeof board !== 'object' || board === null) return [];
  const hidden = (board as Record<string, unknown>)['hidden'];
  return Array.isArray(hidden) ? hidden.filter((entry): entry is string => typeof entry === 'string') : [];
}

/**
 * Writes the middle columns and applies renames everywhere a column's name
 * lives in the document: the board list itself, the hidden list and every
 * agent's `columns` declaration. One write, because a rename that only
 * touched one of those would leave agents pointing at a column that no
 * longer exists.
 */
export function writeBoardColumns(
  document: Doc,
  columns: readonly string[],
  renames: ReadonlyMap<string, string>,
  hidden: readonly string[] = readHiddenColumns(document),
): Doc {
  const next = structuredClone(document) as Doc;
  next.spec ??= {};

  const renamed = (value: string): string => renames.get(value) ?? value;

  const cleaned = columns.map((column) => column.trim()).filter((column) => column.length > 0);
  const hiddenCleaned = [...new Set(hidden.map((column) => renamed(column.trim())).filter((column) => column.length > 0))];
  const board: Record<string, unknown> = {
    ...(cleaned.length > 0 ? { columns: cleaned } : {}),
    ...(hiddenCleaned.length > 0 ? { hidden: hiddenCleaned } : {}),
  };
  if (Object.keys(board).length === 0) delete next.spec.board;
  else next.spec.board = board;

  if (Array.isArray(next.spec.roles)) {
    for (const role of next.spec.roles as Record<string, unknown>[]) {
      const declared = role['columns'];
      if (typeof declared !== 'object' || declared === null) continue;
      const record = declared as Record<string, unknown>;
      const originalFrom = typeof record['from'] === 'string' ? record['from'] : '';
      const originalWorking =
        typeof record['working'] === 'string'
          ? record['working']
          : originalFrom.length === 0
            ? ''
            : deriveWorkingColumn(originalFrom);
      for (const key of ['from', 'pass', 'fail', 'escalate']) {
        if (typeof record[key] === 'string') record[key] = renamed(record[key] as string);
      }
      if (typeof record['working'] === 'string') {
        record['working'] = renamed(record['working'] as string);
      } else if (originalWorking.length > 0) {
        const desiredWorking = renamed(originalWorking);
        const nextFrom = typeof record['from'] === 'string' ? record['from'] : '';
        if (nextFrom.length === 0 || deriveWorkingColumn(nextFrom) !== desiredWorking) {
          record['working'] = desiredWorking;
        }
      }
      if (Array.isArray(record['exits'])) {
        for (const exit of record['exits'] as Record<string, unknown>[]) {
          if (typeof exit['to'] === 'string') exit['to'] = renamed(exit['to'] as string);
        }
      }
    }
  }

  return next;
}

/** Which agents mention each column — what makes a column undeletable. */
export function columnReferences(document: Doc): ReadonlyMap<string, readonly string[]> {
  const references = new Map<string, string[]>();
  const note = (column: string, roleId: string): void => {
    if (column.length === 0) return;
    const list = references.get(column) ?? [];
    if (!list.includes(roleId)) list.push(roleId);
    references.set(column, list);
  };

  for (const role of asArray(document.spec?.roles)) {
    const roleId = text(role, 'id') || '(unnamed)';
    const declared = role['columns'];
    if (typeof declared !== 'object' || declared === null) continue;
    const from = text(declared, 'from');
    note(from, roleId);
    note(text(declared, 'working') || (from.length === 0 ? '' : deriveWorkingColumn(from)), roleId);
    for (const key of ['pass', 'fail', 'escalate']) note(text(declared, key), roleId);
    const record = declared as Record<string, unknown>;
    if (Array.isArray(record['exits'])) {
      for (const exit of record['exits'] as unknown[]) note(text(exit, 'to'), roleId);
    }
  }
  return references;
}

/** Keep the editor's projection identical to the compiler's supported default. */
function deriveWorkingColumn(from: string): string {
  return from.startsWith('to-') ? `in-${from.slice(3)}` : `in-${from}`;
}

export function serializeTeamDocument(document: Doc): string {
  // Wide, because §13.1 documents contain long refs and prompts, and a folded
  // line in YAML is a line somebody will later edit by hand and break.
  return stringify(document, { lineWidth: 0 });
}

/*
 * The model picker's options no longer live here. They come from the platform's
 * own provider registry, served per harness at `GET /v1/harnesses` (each
 * harness carries its `models`), so the picker cannot drift from what the
 * platform can actually start — a hand-kept list here had done exactly that.
 * The card keeps an «other…» escape into free text for whatever ships next.
 */

/** A role with nothing filled in, ready for the operator to name. */
export function blankRole(team: TeamEdit): RoleEdit {
  return {
    draftKey: nextRoleDraftKey(team),
    id: '',
    packageRef: 'builtin://coder@1',
    harness: 'claude',
    connection: '',
    providerRoute: team.providerRoutes[0] ?? '',
    concurrency: 1,
    threadPolicy: 'persistent',
    workspace: 'none',
    environment: '',
    instructions: '',
    model: '',
    toolMode: 'default',
    toolAllow: [],
    toolDeny: [],
    workflowMode: 'background',
    colFrom: '',
    colWorking: '',
    colMaxFails: null,
    exits: [],
  };
}

/** The same blank-row transition the editor's «Add an agent» control uses. */
export function addBlankRoleDraft(team: TeamEdit): TeamEdit {
  return { ...team, roles: [...team.roles, blankRole(team)] };
}

/** The same copy-row transition the editor's «Duplicate» control uses. */
export function duplicateRoleDraft(team: TeamEdit, index: number): TeamEdit {
  const role = team.roles[index];
  if (role === undefined) return team;
  return {
    ...team,
    roles: [
      ...team.roles.slice(0, index + 1),
      { ...role, draftKey: blankRole(team).draftKey, id: duplicateRoleName(role.id, team.roles.map((entry) => entry.id)) },
      ...team.roles.slice(index + 1),
    ],
  };
}

function duplicateRoleName(base: string, taken: readonly string[]): string {
  const stem = base.replace(/-\d+$/u, '') || 'agent';
  for (let number = 2; ; number += 1) {
    const candidate = `${stem}-${String(number)}`;
    if (!taken.includes(candidate)) return candidate;
  }
}

function nextRoleDraftKey(team: TeamEdit): string {
  const taken = new Set(team.roles.map((role) => role.draftKey));
  let number = 1;
  while (taken.has(`draft-${String(number)}`)) number += 1;
  return `draft-${String(number)}`;
}

/**
 * Ready-made agents for the «add an agent» picker.
 *
 * A template is a fully filled role — name, columns, exit rules and an
 * onboarding prompt — so pressing one produces an agent that could be saved
 * as-is. Nothing here touches credentials or connections: those are granted
 * on the card, per team, never baked into a template.
 */
export interface AgentTemplate {
  readonly key: string;
  readonly title: string;
  /** One sentence for the picker card: what this agent does. */
  readonly blurb: string;
  readonly role: Omit<
    RoleEdit,
    | 'draftKey' | 'packageRef' | 'harness' | 'connection' | 'providerRoute' | 'concurrency' | 'threadPolicy' | 'workspace' | 'environment' | 'model' | 'workflowMode' | 'colMaxFails' | 'toolMode' | 'toolAllow' | 'toolDeny'
  >;
}

export const AGENT_TEMPLATES: readonly AgentTemplate[] = [
  {
    key: 'planner',
    title: 'Planner',
    blurb: 'Turns raw requests into tickets a developer can start.',
    role: {
      id: 'planner',
      instructions: [
        '## Fill in before saving',
        '- Repository: [[FILL: git URL, e.g. git@github.com:team/app.git]]',
        '- Base branch: [[FILL: e.g. main or dev]]',
        '- Where requirements come from: [[FILL: who writes raw requests and what they usually miss]]',
        '',
        '## Who you are',
        'You are the team’s planner. The ticket is the team’s central artifact: every other agent reads it before touching anything, so your output is the quality ceiling for everyone downstream.',
        '',
        '## How you work',
        '1. Take the top card from your inbox column and read everything on it: description, comments, attachments.',
        '2. Rewrite it into a ticket a developer can start without asking questions:',
        '   - **Goal** — one paragraph, in the user’s language, saying what changes for whom.',
        '   - **Context** — the relevant code areas, links, prior tickets; clone the repository into your home if you need to check.',
        '   - **Definition of done** — checkable criteria, one per line. «An open merge request implementing X» is the normal final criterion.',
        '   - **Out of scope** — what this ticket deliberately does not cover.',
        '3. **Match the effort to the task.** For a trivial, already-clear request, leave one short scope-and-done comment instead of the full brief — ceremony around a two-line fix costs more than it protects.',
        '4. Write the brief into the ticket itself (edit the description or leave one structured comment). The ticket must stand on its own.',
        '5. Route to a human only on real ambiguity: two materially different readings stay plausible and nothing on the ticket picks one. (A merely large or hard task is not ambiguity.) Ask **one precise question** in a ticket comment — never a bare «unclear» — and send the card to a human.',
        '',
        '## What done means',
        'The ticket is self-sufficient: a developer who has never seen the request can implement it from the ticket alone.',
      ].join('\n'),
      colFrom: 'backlog',
      colWorking: '',
      exits: [
        { when: 'the ticket is ready for a developer', to: 'ready-for-development' },
        { when: 'a person has to decide something first', to: 'human-review' },
      ],
    },
  },
  {
    key: 'coder',
    title: 'Coder',
    blurb: 'Implements the ticket on a feature branch and opens a merge request.',
    role: {
      id: 'coder',
      instructions: [
        '## Fill in before saving',
        '- Repository: [[FILL: git URL, e.g. git@github.com:team/app.git]]',
        '- Git hosting: [[FILL: github or gitlab]]',
        '- Base branch (feature branches start from it): [[FILL: e.g. main or dev]]',
        '- How to run the tests: [[FILL: e.g. npm test]]',
        '- How to open a merge request from the shell: [[FILL: e.g. gh pr create --fill --base main  |  glab mr create --fill --target-branch main]]',
        '',
        '## Who you are',
        'You implement tickets. The ticket is your only source of truth about *what* to do; these instructions say *how*. Your finished work is an **open merge request** — not merged, not deployed: opening it is where your job ends and review begins.',
        '',
        '## How you work',
        '1. **Read the ticket first** — description, definition of done, every comment. If it is blocked or truly ambiguous, never write a bare «blocked»: quote the exact command and the exact error, add one line of what it means, and take the «blocked» exit instead of guessing.',
        '2. **Prepare the repository** in your home folder: clone it if it is not there yet, then `git fetch` and start from the freshest base branch.',
        '3. **Create a feature branch** from the base branch, named `agent/<ticket-id>-<short-slug>` — e.g. `agent/TB-42-password-reset`. One ticket, one branch. When a ticket comes back after review, continue on that same branch and the same MR — never open a second MR for one ticket.',
        '4. **Implement** the smallest change that satisfies the definition of done. For a bug or a behaviour change, first write the failing test that reproduces it, watch it fail, then make it pass. Follow the repository’s own conventions — read neighbouring code before writing yours.',
        '5. **Verify**: run the test command above; add tests for what you changed. Do not open a merge request with failing tests — fix them or report why they cannot pass.',
        '6. **Open the merge request**: push the branch, open an MR against the base branch. Title: `<ticket-id>: <what it does>`. Description: what changed, why, how it was verified, and any follow-ups — assume the reviewer has not read the ticket.',
        '7. **Read back what you wrote**: a command that «succeeded» is not evidence. After the push, confirm the branch exists on the remote; after opening the MR, fetch its URL and see it answer. Only what you read back counts as done.',
        '8. **Attach the MR to the ticket**: post the MR link as a ticket comment, structured as *what the ticket asked — what changed — how it was verified*, and name the exact commit you verified (`git rev-parse HEAD`). The comment must read on its own, without the diff or the ticket history. The next agent finds your work through the ticket, nowhere else.',
        '',
        '## What done means',
        'An open merge request that implements the definition of done, with green tests, linked from the ticket. Review conversation happens in the MR; anything about scope goes back to the ticket.',
      ].join('\n'),
      colFrom: 'ready-for-development',
      colWorking: 'in-development',
      exits: [
        { when: 'the merge request is open, tests pass, and the link is on the ticket', to: 'needs-review' },
        { when: 'blocked or the ticket is unclear', to: 'human-review' },
      ],
    },
  },
  {
    key: 'reviewer',
    title: 'Reviewer',
    blurb: 'Reviews the merge request against the ticket before it counts.',
    role: {
      id: 'reviewer',
      instructions: [
        '## Fill in before saving',
        '- Repository: [[FILL: git URL]]',
        '- Git hosting: [[FILL: github or gitlab]]',
        '- How to check out a merge request locally: [[FILL: e.g. gh pr checkout <number>  |  glab mr checkout <number>]]',
        '- Review bar specific to this team: [[FILL: what must never pass review — e.g. no new dependencies without a note, no skipped tests]]',
        '',
        '## Who you are',
        'You review merge requests as a strict but fair colleague. The ticket defines what the change must do; the merge request is where the code conversation lives; the ticket is where the verdict lands.',
        '',
        '## How you work',
        '1. **Read the ticket** — goal and definition of done. A technically fine MR that does not do what the ticket asks is a rejection.',
        '2. **Find the MR** through the ticket (the coder links it there). Check out its branch in your home repository and note the exact commit you are reviewing.',
        '3. **Form your own findings first**, then read other reviewers’ comments and reconcile — a review that starts from someone else’s notes is an echo, not a review.',
        '4. **Review in this order**: correctness against the definition of done → tests (do they exist, do they pass, do they test the change) → readability and fit with the surrounding code.',
        '5. **Comment on the MR** for everything code-level: point at files and lines, say why each issue matters, suggest the fix. The code conversation belongs in the MR.',
        '6. **Write the verdict on the ticket** as a comment: approved, or a short list of what must change — and name the commit it applies to. New commits on the branch void the verdict: review the new diff, never your memory of the old one.',
        '7. Approve only what you would ship yourself. Merging is a decision for a person unless the team has said otherwise.',
        '',
        '## What done means',
        'The MR carries your line comments; the ticket carries your verdict; the card takes the exit that matches it.',
      ].join('\n'),
      colFrom: 'needs-review',
      colWorking: 'in-review',
      exits: [
        { when: 'approved — the change does what the ticket asks', to: 'done' },
        { when: 'changes are needed; the list is on the ticket and the MR', to: 'ready-for-development' },
      ],
    },
  },
  {
    key: 'tester',
    title: 'Tester',
    blurb: 'Verifies the change like a user and files reproducible findings.',
    role: {
      id: 'tester',
      instructions: [
        '## Fill in before saving',
        '- Repository: [[FILL: git URL]]',
        '- How to run the app locally from a branch: [[FILL: e.g. npm install && npm run dev, then open http://localhost:3000]]',
        '- How to check out a merge request locally: [[FILL: e.g. gh pr checkout <number>]]',
        '',
        '## Who you are',
        'You verify changes like a user would, not like their author. The ticket says what should work; your findings — pass or fail — land on the ticket with evidence.',
        '',
        '## How you work',
        '1. **Read the ticket**: the definition of done is your test plan’s spine. Add the obvious edge cases around it.',
        '2. **Get the change**: find the MR through the ticket, check out its branch in your home, note the exact commit — your verdict is about that commit and should name it. Run the app with the command above.',
        '3. **Test and record as you go**: exact steps, expected, actual. For anything user-visible the evidence is an artifact — a screenshot or recording; prose alone is not visual evidence. Attach it to the ticket.',
        '4. **A bug report must be reproducible**: steps, expected, actual, evidence. Vague findings are findings about your report, not about the change.',
        '5. **Write the summary on the ticket**: what you tested on which commit, what passed, what failed, with the evidence attached — readable without opening the MR.',
        '',
        '## What done means',
        'The ticket carries a verdict a stranger can trust: either «verified, here is what I did», or a reproducible failure the coder can start on immediately.',
      ].join('\n'),
      colFrom: 'testing',
      colWorking: 'in-testing',
      exits: [
        { when: 'it works — evidence is on the ticket', to: 'done' },
        { when: 'something is broken — a reproducible report is on the ticket', to: 'ready-for-development' },
      ],
    },
  },
  {
    key: 'researcher',
    title: 'Researcher',
    blurb: 'Answers a question with evidence before anybody builds.',
    role: {
      id: 'researcher',
      instructions: [
        '## Fill in before saving',
        '- Repository: [[FILL: git URL, if code questions are in scope]]',
        '- Sources that count for this team: [[FILL: e.g. our docs site, the vendor’s changelog, the codebase itself]]',
        '',
        '## Who you are',
        'You answer the question on the ticket with evidence, so that whoever builds next starts from facts. The ticket is both your input and your output.',
        '',
        '## How you work',
        '1. **Read the ticket** and restate the question in one sentence at the top of your findings — if you cannot, the ticket goes back for sharpening.',
        '2. **Investigate**: read the code (clone into your home), the docs, whatever the ticket links. Prefer primary sources.',
        '3. **Write findings on the ticket**, structured: what is true (with where each claim comes from — file, link, command output), what is unknown, what you recommend and why.',
        '4. Keep raw materials — longer notes, extracts — in your home’s notes folder; the ticket gets the distilled version with pointers.',
        '',
        '## What done means',
        'A teammate can act on the ticket without redoing your research: claims are sourced, unknowns are named, the recommendation is explicit.',
      ].join('\n'),
      colFrom: 'research',
      colWorking: 'in-research',
      exits: [
        { when: 'the question is answered on the ticket', to: 'done' },
        { when: 'a person has to weigh in', to: 'human-review' },
      ],
    },
  },
];

/**
 * Rebases a screen's edit onto the team that is running now.
 *
 * Three-way, per agent and per connection, keyed by id: what the form added
 * appears, what it deleted disappears, what it changed wins over the running
 * version of the same item — and everything the form never touched keeps the
 * running version's shape. This is what lets a tab opened an hour ago press
 * Save without either resurrecting an old team or being told to start over.
 */
export function rebaseTeamEdit(form: TeamEdit, base: TeamEdit, active: TeamEdit): TeamEdit {
  const same = (a: unknown, b: unknown): boolean => JSON.stringify(a) === JSON.stringify(b);

  function mergeById<T extends { readonly id: string }>(
    formList: readonly T[],
    baseList: readonly T[],
    activeList: readonly T[],
  ): readonly T[] {
    const baseBy = new Map(baseList.map((item) => [item.id, item]));
    const formBy = new Map(formList.map((item) => [item.id, item]));

    // Start from what runs; drop what the form deliberately deleted.
    const kept = activeList.filter((item) => !(baseBy.has(item.id) && !formBy.has(item.id)));

    const out: T[] = kept.map((item) => {
      const inForm = formBy.get(item.id);
      if (inForm === undefined) return item;
      const inBase = baseBy.get(item.id);
      // The form's version wins when the form actually changed it.
      return inBase === undefined || !same(inForm, inBase) ? inForm : item;
    });

    // What the form added (or what active never had) comes last, in form order.
    for (const item of formList) {
      if (!out.some((existing) => existing.id === item.id)) out.push(item);
    }
    return out;
  }

  return {
    name: form.name !== base.name ? form.name : active.name,
    roles: mergeById(form.roles, base.roles, active.roles),
    configuredAgentIds: form.configuredAgentIds,
    integrations: mergeById(form.integrations, base.integrations, active.integrations),
    providerRoutes: active.providerRoutes,
    workspaces: active.workspaces,
  };
}

/** A template turned into a real role for this team; the base fills the rest. */
export function roleFromTemplate(template: AgentTemplate, team: TeamEdit): RoleEdit {
  return { ...blankRole(team), ...template.role, workflowMode: 'board-worker' };
}

/**
 * Makes the role's board participation explicit. Going background deliberately
 * clears column values so a later save cannot preserve hidden workflow wiring;
 * returning to the board creates the smallest useful, editable starting point.
 */
export function setRoleWorkflowMode(role: RoleEdit, workflowMode: RoleEdit['workflowMode']): RoleEdit {
  if (role.workflowMode === workflowMode) return role;
  if (workflowMode === 'background') {
    return { ...role, workflowMode, colFrom: '', colWorking: '', colMaxFails: null, exits: [] };
  }
  return {
    ...role,
    workflowMode,
    colFrom: role.colFrom.trim().length === 0 ? 'backlog' : role.colFrom,
    exits: role.exits.length === 0 ? [{ when: 'the work is done', to: 'done' }] : role.exits,
  };
}

/** An MCP integration with the shape most servers use. */
export function blankIntegration(): IntegrationEdit {
  return {
    id: '',
    type: 'mcp',
    connectionRef: 'secretref://integrations/',
    roles: [],
    endpoints: [],
    command: 'npx',
    url: '',
    args: ['-y'],
    env: [],
  };
}

/**
 * The obvious mistakes, restated on the client.
 *
 * A courtesy and not a gate: the compiler is authoritative (AC-001), and every
 * rule below is one it already enforces. They exist so the loop is short while
 * somebody is typing, and the screen says plainly that the server decides.
 */
export function checkLocally(team: TeamEdit): readonly string[] {
  const problems: string[] = [];

  const editableRoleIds = new Set(team.roles.map((role) => role.id));
  const ids = new Set<string>(team.configuredAgentIds.filter((id) => !editableRoleIds.has(id)));
  for (const role of team.roles) {
    if (role.id.trim().length === 0) problems.push('An agent with no name cannot be saved.');
    else if (ids.has(role.id)) problems.push(`Two agents are called "${role.id}".`);
    ids.add(role.id);

    if (role.instructions.includes('[[FILL:')) {
      problems.push(
        `${role.id || 'an agent'}: the instructions still contain [[FILL: …]] placeholders — fill them in (or delete the line) before saving.`,
      );
    }

    if (role.providerRoute.length === 0) {
      problems.push(`${role.id || 'an agent'} names no provider route.`);
    } else if (!team.providerRoutes.includes(role.providerRoute)) {
      problems.push(`${role.id}: there is no provider route called "${role.providerRoute}".`);
    }

    if (!team.workspaces.includes(role.workspace)) {
      problems.push(`${role.id}: there is no workspace called "${role.workspace}".`);
    }

    if (role.workflowMode === 'board-worker') {
      const label = role.id || 'an agent';
      if (role.colFrom.trim().length === 0) {
        problems.push(`${label}: a board worker needs a Takes from column.`);
      }
      if (!role.exits.some((exit) => exit.when.trim().length > 0 && exit.to.trim().length > 0)) {
        problems.push(`${label}: a board worker needs at least one complete exit (condition and destination).`);
      }
    }
  }

  for (const integration of team.integrations) {
    if (integration.id.trim().length === 0) problems.push('An integration with no name cannot be saved.');
    if (!integration.connectionRef.startsWith('secretref://')) {
      // §13.2: a credential is referenced, never written. The compiler rejects
      // the document; saying so here saves a round trip.
      problems.push(`${integration.id || 'an integration'}: the connection must be a secretref:// reference.`);
    }
    if (integration.roles.length === 0) {
      problems.push(`${integration.id || 'an integration'} is bound to no agent, so nothing can use it.`);
    }
    for (const roleId of integration.roles) {
      // The Main Operator is a first-class MCP principal rather than a stage
      // or background-agent row. Keep the courtesy validator aligned with the
      // compiler's deliberate MCP-only compatibility rule.
      if (roleId === 'main-operator' && integration.type === 'mcp') continue;
      if (!ids.has(roleId)) problems.push(`${integration.id}: there is no agent called "${roleId}".`);
    }
    if (integration.type === 'mcp' && integration.command.trim().length === 0 && integration.url.trim().length === 0) {
      problems.push(`${integration.id}: an MCP server needs a command or remote URL.`);
    }
    if (integration.type === 'mcp' && integration.command.trim().length > 0 && integration.url.trim().length > 0) {
      problems.push(`${integration.id}: choose either a command or remote URL, not both.`);
    }
    for (const entry of integration.env) {
      if (entry.value.length > 0 && !entry.value.startsWith('secretref://') && looksLikeCredential(entry.value)) {
        problems.push(`${integration.id}: ${entry.name} looks like a credential; write a secretref:// reference.`);
      }
    }
  }

  return problems;
}

/**
 * A credential shape, roughly.
 *
 * The compiler's canary suite is the real check and it is far better than this.
 * This one exists to catch the paste, in the field, while it is happening.
 */
function looksLikeCredential(value: string): boolean {
  return /^(gh[pousr]_|glpat-|sk-|xox[baprs]-)/u.test(value) || (value.length > 32 && !value.includes(' '));
}

function asArray(value: unknown): readonly Record<string, unknown>[] {
  return Array.isArray(value) ? value.filter((entry): entry is Record<string, unknown> => typeof entry === 'object' && entry !== null) : [];
}

function text(value: unknown, key: string): string {
  if (typeof value !== 'object' || value === null) return '';
  const found = (value as Record<string, unknown>)[key];
  return typeof found === 'string' ? found : '';
}

function readRole(role: Record<string, unknown>, draftKey: string): RoleEdit {
  const placement = role['placement'];
  const toolAccess = typeof role['toolAccess'] === 'object' && role['toolAccess'] !== null ? role['toolAccess'] as Record<string, unknown> : null;
  return {
    raw: role,
    draftKey,
    id: text(role, 'id'),
    packageRef: text(role, 'package'),
    // The compiler's default, restated here so the picker has a value to show
    // for a document written before harnesses were a field.
    harness: text(role, 'harness') || 'claude',
    connection: text(role, 'connection'),
    providerRoute: text(role, 'providerRoute'),
    concurrency: typeof role['concurrency'] === 'number' ? role['concurrency'] : 1,
    threadPolicy: (text(role, 'threadPolicy') || 'persistent') as RoleEdit['threadPolicy'],
    workspace: text(role, 'workspace') || 'none',
    environment: text(placement, 'environment'),
    instructions: text(role, 'instructions'),
    model: text(role, 'model'),
    toolMode: toolAccess === null ? 'default' : 'custom',
    toolAllow: toolAccess !== null && Array.isArray(toolAccess['allow']) ? toolAccess['allow'].filter((value): value is string => typeof value === 'string') : [],
    toolDeny: toolAccess !== null && Array.isArray(toolAccess['deny']) ? toolAccess['deny'].filter((value): value is string => typeof value === 'string') : [],
    workflowMode: hasColumns(role) ? 'board-worker' : 'background',
    colFrom: text(role['columns'], 'from'),
    colWorking: text(role['columns'], 'working'),
    colMaxFails: number(role['columns'], 'maxFails'),
    exits: readExits(role['columns']),
  };
}

function hasColumns(role: Record<string, unknown>): boolean {
  const columns = role['columns'];
  return typeof columns === 'object' && columns !== null;
}

function number(value: unknown, key: string): number | null {
  if (typeof value !== 'object' || value === null) return null;
  const found = (value as Record<string, unknown>)[key];
  return typeof found === 'number' ? found : null;
}

/** Rules from the document — the list, or the legacy pass/fail sugar. */
function readExits(columns: unknown): readonly { when: string; to: string; limit?: number }[] {
  if (typeof columns !== 'object' || columns === null) return [];
  const record = columns as Record<string, unknown>;
  if (Array.isArray(record['exits'])) {
    return (record['exits'] as unknown[])
      .map((exit) => {
        const limit = number(exit, 'limit');
        return { when: text(exit, 'when'), to: text(exit, 'to'), ...(limit === null ? {} : { limit }) };
      })
      .filter((exit) => exit.to.length > 0);
  }
  const legacy: { when: string; to: string; limit?: number }[] = [];
  if (typeof record['pass'] === 'string') legacy.push({ when: 'the work is done', to: record['pass'], limit: 3 });
  if (typeof record['fail'] === 'string') {
    legacy.push({ when: 'the work needs another pass', to: record['fail'], limit: number(record, 'maxFails') ?? 3 });
  }
  return legacy;
}

function readIntegration(integration: Record<string, unknown>): IntegrationEdit {
  const env = integration['env'];
  return {
    id: text(integration, 'id'),
    type: (text(integration, 'type') || 'mcp') as IntegrationEdit['type'],
    connectionRef: text(integration, 'connectionRef'),
    roles: strings(integration['roles']),
    endpoints: strings(integration['endpoints']),
    command: text(integration, 'command'),
    url: text(integration, 'url'),
    args: strings(integration['args']),
    env:
      typeof env === 'object' && env !== null
        ? Object.entries(env as Record<string, unknown>).map(([name, value]) => ({ name, value: String(value) }))
        : [],
  };
}

function strings(value: unknown): readonly string[] {
  return Array.isArray(value) ? value.filter((entry): entry is string => typeof entry === 'string') : [];
}
